import { useEffect, type RefObject } from 'react'
import type { SceneApi } from './CoverScene'

export interface HotkeyActions {
  /** Nudge the light azimuth by ±degrees. */
  nudgeLightAzimuth(delta: number): void
  /** Nudge the light elevation by ±degrees. */
  nudgeLightElevation(delta: number): void
  toggleHelp(): void
}

const ROTATE_STEP = 8 // degrees
const ZOOM_IN = 0.9
const ZOOM_OUT = 1.1
const LIGHT_STEP = 12 // degrees

/**
 * Desktop keyboard controls (mouse/touch remain primary):
 *   arrows  rotate     +/- zoom     [ ] light azimuth     ; ' light elevation
 *   R reset view       ? toggle help overlay
 */
export function useHotkeys(apiRef: RefObject<SceneApi | null>, actions: HotkeyActions, enabled = true) {
  useEffect(() => {
    if (!enabled) return
    const onKey = (e: KeyboardEvent) => {
      const api = apiRef.current
      // Ignore when typing into a form control.
      const t = e.target as HTMLElement | null
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return

      switch (e.key) {
        case 'ArrowLeft':
          api?.rotate(-ROTATE_STEP, 0)
          break
        case 'ArrowRight':
          api?.rotate(ROTATE_STEP, 0)
          break
        case 'ArrowUp':
          api?.rotate(0, ROTATE_STEP)
          break
        case 'ArrowDown':
          api?.rotate(0, -ROTATE_STEP)
          break
        case '+':
        case '=':
          api?.zoom(ZOOM_IN)
          break
        case '-':
        case '_':
          api?.zoom(ZOOM_OUT)
          break
        case '[':
          actions.nudgeLightAzimuth(-LIGHT_STEP)
          break
        case ']':
          actions.nudgeLightAzimuth(LIGHT_STEP)
          break
        case ';':
          actions.nudgeLightElevation(-LIGHT_STEP)
          break
        case "'":
          actions.nudgeLightElevation(LIGHT_STEP)
          break
        case 'r':
        case 'R':
          api?.resetView()
          break
        case '?':
          actions.toggleHelp()
          break
        default:
          return
      }
      e.preventDefault()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [apiRef, actions, enabled])
}
